import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  parseFactoredRational,
  generateRationalGraphVariations,
  rationalToCurveElement,
  defaultRationalGraphConfig,
  normalizeStep1Types,
  sanitizeStep1Types,
  suggestXMin,
  suggestXMax,
  suggestYMin,
  suggestYMax,
  rationalEquationToLatex,
} from './rationalGraphUtils';

/**
 * Admin component for creating/editing rational graph questions
 *
 * Props:
 * - config: RationalGraphConfig - Current configuration
 * - onConfigChange: (config) => void - Called when config changes
 * - correctAnswer: { correctGraphIndex: number } - Correct answer
 * - onAnswerChange: (answer) => void - Called when answer changes
 */
const RationalGraphAdmin = ({
  config = defaultRationalGraphConfig,
  onConfigChange,
  correctAnswer,
  onAnswerChange,
}) => {
  const [equationInput, setEquationInput] = useState(config.equation || '');
  const [parseError, setParseError] = useState('');
  const [variations, setVariations] = useState([]);
  const [gridError, setGridError] = useState('');

  // Normalize step1Types for backward compatibility
  const step1Types = normalizeStep1Types(config);

  // Keep latest callbacks in refs so effects always call the current version
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;
  const onAnswerChangeRef = useRef(onAnswerChange);
  onAnswerChangeRef.current = onAnswerChange;

  // Compute parsed data from equation for display — independent of parent round-trip
  // Server validation only keeps numeratorFactors/denominatorFactors with root/multiplicity,
  // stripping derived display fields (xIntercepts, verticalAsymptotes, yIntercept, etc.).
  // So we must parse from equation whenever derived fields are missing.
  const parsedRationalDisplay = useMemo(() => {
    // Check if config already has FULL parsed data (not just raw factors from server)
    const hasFullParsedData = config.parsedRational?.xIntercepts !== undefined;
    if (hasFullParsedData) return config.parsedRational;

    // Parse from equation to get full derived data
    const eq = equationInput || config.equation;
    if (!eq) return config.parsedRational;
    try {
      return parseFactoredRational(eq);
    } catch {
      return config.parsedRational;
    }
  }, [equationInput, config.equation, config.parsedRational]);

  // STATE RESTORATION + AUTO-PARSE on mount
  useEffect(() => {
    if (!config.equation) return;

    const hasFullParsedData = config.parsedRational?.xIntercepts !== undefined;

    if (hasFullParsedData && config.variationSeed) {
      // Existing fully-parsed config — just regenerate variations
      setEquationInput(config.equation);
      try {
        const types = normalizeStep1Types(config);
        const { variations: vars } = generateRationalGraphVariations(
          config.parsedRational,
          types,
          config.variationSeed
        );
        setVariations(vars);
        setParseError('');
      } catch (e) {
        setParseError('Failed to restore question configuration');
      }
    } else {
      // Equation present but missing full parsed data or seed — auto-parse and persist
      setEquationInput(config.equation);
      try {
        const parsed = parseFactoredRational(config.equation);
        const types = normalizeStep1Types(config);
        const seed = Date.now();
        const { variations: vars, correctIndex } = generateRationalGraphVariations(
          parsed,
          types,
          seed
        );
        setVariations(vars);
        setParseError('');
        onConfigChangeRef.current({
          ...config,
          equation: config.equation,
          parsedRational: parsed,
          step1Types: types,
          variationSeed: seed,
          correctGraphIndex: correctIndex,
        });
        onAnswerChangeRef.current({ correctGraphIndex: correctIndex });
      } catch (e) {
        setParseError(e.message);
      }
    }
  }, []); // Only run on mount

  // Parse equation when input changes
  const handleEquationChange = useCallback((value) => {
    setEquationInput(value);

    if (!value.trim()) {
      setParseError('');
      setVariations([]);
      return;
    }

    try {
      const parsed = parseFactoredRational(value);
      setParseError('');

      // Auto-remove step types the parsed rational cannot answer (no x-intercepts,
      // undefined y-intercept, oblique asymptote, or — for quad-only denominators
      // like 6/(x²+1) — zero vertical asymptotes) and pick an applicable fallback.
      const currentTypes = sanitizeStep1Types(parsed, step1Types);

      // Generate variations with new seed
      const seed = Date.now();
      const { variations: vars, correctIndex } = generateRationalGraphVariations(
        parsed,
        currentTypes,
        seed
      );
      setVariations(vars);

      // Update config
      onConfigChange({
        ...config,
        equation: value,
        parsedRational: parsed,
        step1Types: currentTypes,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      // Update answer
      onAnswerChange({ correctGraphIndex: correctIndex });

    } catch (e) {
      setParseError(e.message);
      setVariations([]);
    }
  }, [config, step1Types, onConfigChange, onAnswerChange]);

  // Regenerate variations when step1 type checkboxes change
  const handleStep1TypeToggle = useCallback((typeKey) => {
    let newTypes;
    if (step1Types.includes(typeKey)) {
      // Uncheck — but don't allow empty
      newTypes = step1Types.filter(t => t !== typeKey);
      if (newTypes.length === 0) return;
    } else {
      // Check — max 2
      if (step1Types.length >= 2) return;
      newTypes = [...step1Types, typeKey];
    }

    if (config.parsedRational) {
      const seed = Date.now();
      const { variations: vars, correctIndex } = generateRationalGraphVariations(
        config.parsedRational,
        newTypes,
        seed
      );
      setVariations(vars);

      onConfigChange({
        ...config,
        step1Types: newTypes,
        interceptType: undefined,
        variationSeed: seed,
        correctGraphIndex: correctIndex,
      });

      onAnswerChange({ correctGraphIndex: correctIndex });
    } else {
      onConfigChange({
        ...config,
        step1Types: newTypes,
        interceptType: undefined,
      });
    }
  }, [config, step1Types, onConfigChange, onAnswerChange]);

  // Handle grid config changes with validation
  const handleGridChange = useCallback((key, value) => {
    const newGrid = {
      ...config.grid,
      [key]: value,
    };

    if (newGrid.xMin >= newGrid.xMax) {
      setGridError('X Min must be less than X Max');
    } else if (newGrid.yMin >= newGrid.yMax) {
      setGridError('Y Min must be less than Y Max');
    } else {
      setGridError('');
    }

    onConfigChange({
      ...config,
      grid: newGrid,
    });
  }, [config, onConfigChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Section 1: Equation Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          1. Enter Rational Function
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the rational function in factored form. Examples:
          <br />
          - f(x) = (x+2)(x-3) / (x+1)^2(x-2)
          <br />
          - f(x) = 1/2(x+3)(x-1) / (x-2)(x+4)
          <br />
          - f(x) = -(x+1) / (x-2)^2
          <br />
          - y = 6/x  (constant numerator / bare x is allowed)
        </Typography>

        <TextField
          fullWidth
          label="Rational Function"
          value={equationInput}
          onChange={(e) => handleEquationChange(e.target.value)}
          error={!!parseError}
          helperText={parseError || 'Use / to separate numerator and denominator, ^ for exponents'}
          placeholder="f(x) = (x+2)(x-3) / (x+1)^2(x-2)"
        />

        {/* LaTeX Preview */}
        {equationInput && !parseError && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <Box sx={{ fontSize: '1.2rem', mt: 1 }}>
              <span dangerouslySetInnerHTML={{
                __html: katex.renderToString(rationalEquationToLatex(equationInput), { throwOnError: false })
              }} />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Section 2: Step 1 Type Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          2. Choose Step 1 Type(s)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select 1 or 2 types for students to identify in Step 1.
        </Typography>
        {(() => {
          // Disable step types that don't apply to the current rational.
          // These conditions only fire once an equation has successfully parsed.
          const xDisabled = parsedRationalDisplay
            && Array.isArray(parsedRationalDisplay.xIntercepts)
            && parsedRationalDisplay.xIntercepts.length === 0;
          const yDisabled = parsedRationalDisplay
            && !Number.isFinite(parsedRationalDisplay.yIntercept);
          const haDisabled = parsedRationalDisplay && !parsedRationalDisplay.horizontalAsymptote;
          const vaDisabled = parsedRationalDisplay
            && Array.isArray(parsedRationalDisplay.verticalAsymptotes)
            && parsedRationalDisplay.verticalAsymptotes.length === 0;
          return (
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Tooltip title={xDisabled ? 'No x-intercepts (constant numerator)' : ''}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={step1Types.includes('x')}
                          onChange={() => handleStep1TypeToggle('x')}
                          disabled={xDisabled}
                        />
                      }
                      label="X-intercepts (zeros)"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={yDisabled ? 'No y-intercept (vertical asymptote at x=0)' : ''}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={step1Types.includes('y')}
                          onChange={() => handleStep1TypeToggle('y')}
                          disabled={yDisabled}
                        />
                      }
                      label="Y-intercept"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={vaDisabled ? 'No vertical asymptotes (denominator has no real roots)' : ''}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={step1Types.includes('va')}
                          onChange={() => handleStep1TypeToggle('va')}
                          disabled={vaDisabled}
                        />
                      }
                      label="Vertical Asymptotes"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={haDisabled ? 'No horizontal asymptote (oblique asymptote)' : ''}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={step1Types.includes('ha')}
                          onChange={() => handleStep1TypeToggle('ha')}
                          disabled={haDisabled}
                        />
                      }
                      label="Horizontal Asymptote"
                    />
                  </span>
                </Tooltip>
              </Box>
            </FormControl>
          );
        })()}

        {step1Types.length >= 2 && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
            Maximum 2 types selected.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {(() => {
            const labels = {
              x: 'x-intercepts (zeros)',
              y: 'y-intercept',
              va: 'vertical asymptotes',
              ha: 'horizontal asymptote',
            };
            const desc = step1Types.map(t => labels[t]).join(' and ');
            return `Students will find the ${desc}`;
          })()}
        </Typography>
      </Paper>

      {/* Section 3: Parsed Information */}
      {parsedRationalDisplay && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            3. Parsed Rational Function Information
          </Typography>
          <ParsedRationalSummary rational={parsedRationalDisplay} />
        </Paper>
      )}

      {/* Section 4: Grid Configuration */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          4. Grid Configuration
        </Typography>

        {parsedRationalDisplay && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Suggested grid bounds:</strong> Based on your function's roots and asymptotes,
              consider setting:
              <br />
              X range: [{suggestXMin(parsedRationalDisplay)}, {suggestXMax(parsedRationalDisplay)}]
              <br />
              Y range: [{suggestYMin(parsedRationalDisplay)}, {suggestYMax(parsedRationalDisplay)}]
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <TextField
            type="number"
            label="X Min"
            value={config.grid?.xMin ?? -10}
            onChange={(e) => handleGridChange('xMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="X Max"
            value={config.grid?.xMax ?? 10}
            onChange={(e) => handleGridChange('xMax', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Min"
            value={config.grid?.yMin ?? -10}
            onChange={(e) => handleGridChange('yMin', Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Y Max"
            value={config.grid?.yMax ?? 10}
            onChange={(e) => handleGridChange('yMax', Number(e.target.value))}
          />
        </Box>

        {gridError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {gridError}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tip: Ensure the grid is large enough to show key features (intercepts, asymptotes)
          but small enough that curves are distinguishable.
        </Typography>
      </Paper>

      {/* Section 5: Graph Preview */}
      {variations.length === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            5. Generated Graph Variations
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Graph {variations.find(v => v.isCorrect)?.label} is the correct answer.
            All graphs share the same{' '}
            {(() => {
              const labels = {
                x: 'x-intercepts',
                y: 'y-intercept',
                va: 'vertical asymptotes',
                ha: 'horizontal asymptote',
              };
              return step1Types.map(t => labels[t]).join(' and ');
            })()}.
          </Alert>
          <RationalGraphPreview
            variations={variations}
            gridConfig={config.grid}
          />
        </Paper>
      )}
    </Box>
  );
};

/**
 * Display parsed rational function information
 */
const ParsedRationalSummary = ({ rational }) => {
  const {
    leadingCoefficient,
    xIntercepts = [],
    verticalAsymptotes = [],
    yIntercept,
    horizontalAsymptote,
    numDegree,
    denDegree,
  } = rational;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography>
        <strong>Numerator degree:</strong> {numDegree ?? '—'} | <strong>Denominator degree:</strong> {denDegree ?? '—'}
      </Typography>
      {leadingCoefficient != null && (
        <Typography>
          <strong>Leading Coefficient:</strong> {leadingCoefficient.toFixed(4)}
          {leadingCoefficient > 0 ? ' (positive)' : ' (negative)'}
        </Typography>
      )}

      <Typography><strong>X-intercepts (zeros):</strong></Typography>
      <Box sx={{ pl: 2 }}>
        {xIntercepts.length > 0 ? xIntercepts.map((xi, i) => (
          <Typography key={i}>
            x = {xi.value} (multiplicity {xi.multiplicity}
            {xi.multiplicity % 2 === 0 ? ' - touches' : ' - crosses'})
          </Typography>
        )) : (
          <Typography color="text.secondary">None</Typography>
        )}
      </Box>

      <Typography><strong>Vertical Asymptotes:</strong></Typography>
      <Box sx={{ pl: 2 }}>
        {verticalAsymptotes.length > 0 ? verticalAsymptotes.map((va, i) => (
          <Typography key={i}>
            x = {va.value} (multiplicity {va.multiplicity}
            {va.multiplicity % 2 === 1 ? ' - opposite sides' : ' - same side'})
          </Typography>
        )) : (
          <Typography color="text.secondary">None</Typography>
        )}
      </Box>

      <Typography>
        <strong>Y-intercept:</strong> {yIntercept == null || isNaN(yIntercept) ? 'undefined' : `(0, ${yIntercept.toFixed(4)})`}
      </Typography>

      <Typography>
        <strong>Horizontal Asymptote:</strong>{' '}
        {horizontalAsymptote ? `y = ${horizontalAsymptote.value.toFixed(4)}` : 'None (oblique asymptote)'}
      </Typography>
    </Box>
  );
};

/**
 * Preview all 4 graph variations in a 2x2 grid
 */
const RationalGraphPreview = ({ variations, gridConfig }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2,
      }}
    >
      {variations.map((variation) => (
        <Paper
          key={variation.label}
          sx={{
            p: 1,
            border: variation.isCorrect ? '3px solid green' : '1px solid grey',
            bgcolor: variation.isCorrect ? 'success.light' : 'background.paper',
          }}
        >
          <Typography variant="subtitle2" align="center">
            Graph {variation.label}
            {variation.isCorrect && ' (Correct)'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CoordinateGrid
              config={gridConfig}
              elements={[rationalToCurveElement(variation.rational, gridConfig)]}
              pendingPoints={[]}
              mode="point"
              readOnly={true}
              width={200}
              height={200}
            />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default RationalGraphAdmin;
